import {defineStore} from 'pinia';
import {AUTH_TOKEN_KEY, NOTIFY, PERMISSIONS_TTL_MS, REFRESH_TOKEN, ROLE_ADMIN, TOAST_OPTIONS} from "@/constants";
import api from "@/core/services/axios.js";
import {useUserStore} from "@/modules/user/store/userStore.js";
import {verifyJWT} from "@/core/utils/jwt";
import notifier from "@/core/utils/notifier.js";


export const useAuthStore = defineStore('authStore', {
    state: () => ({
        access_token: localStorage.getItem(AUTH_TOKEN_KEY) || null,
        refresh_token: localStorage.getItem(REFRESH_TOKEN) || null,
        userRoles: [],
        permissionsFetchedAt: null,
        _permissionsPromise: null,
        userId: 0,
        isInitialized: false,
    }),
    actions: {
        async fetchUserPermissions({force = false} = {}) {
            if (!this.access_token || !verifyJWT(this.access_token, 30).valid) {
                console.warn("Нет валидного токена для получения прав");
                return;
            }

            const now = Date.now();
            const fresh = this.permissionsFetchedAt && (now - this.permissionsFetchedAt < PERMISSIONS_TTL_MS);
            if (!force && fresh) {
                return;
            }

            if (this._permissionsPromise) {
                return this._permissionsPromise;
            }

            this._permissionsPromise = (async () => {
                try {
                    // Получаем профиль пользователя, роль берём из ответа бэкенда
                    const response = await api.get('/profile');

                    const role = response.data?.role || 'user';

                    // Маппинг ролей на permissions для обратной совместимости
                    // Все пользователи имеют базовые права
                    const basePermissions = [
                        'create_quick_announcement',
                    ];

                    // Для обычных пользователей даём все права на создание объявлений
                    if (role === 'user' || role === 'admin') {
                        basePermissions.push(
                            'create_vacancy',
                            'create_resume',
                            'create_offering',
                            'create_realty',
                        );
                    }

                    // Дополнительные права для админов
                    if (role === 'admin') {
                        basePermissions.push(
                            'moderate_listing',
                            'moderate_vacancy',
                            'view_messenger',
                            'create_messenger',
                            'update_messenger',
                            'delete_messenger',
                        );
                    }

                    // Преобразуем в формат { permission_name: string }
                    this.userRoles = basePermissions.map(permission => ({
                        permission_name: permission,
                        role_name: role,
                    }));

                    this.permissionsFetchedAt = Date.now();
                } catch (error) {
                    // Игнорируем ошибки отмены запроса
                    if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
                        return;
                    }

                    notifier.error(TOAST_OPTIONS.error, 'Сайт временно не работает. Полный функционал сайта временно не доступен');
                    console.error("Ошибка при получении ролей и прав:", error);
                    this.userRoles = [];
                } finally {
                    this._permissionsPromise = null;
                }
            })();

            return this._permissionsPromise;
        },
        async TokenInfo(token) {
            const info = {
                check: false,
                token: null,
                userRoles: [],
                userId: 0,
            }
            if (!token) {
                return info;
            }

            const verified = verifyJWT(token, 30);
            if (!verified.valid) {
                return info;
            }

            const userId = verified.payload['user_id'] || [];

            info.check = true;
            info.token = token;
            info.userRoles = this.userRoles;
            info.userId = userId;

            return info
        },
        /**
         * Инициализация авторизации приложения.
         *
         * Изменения:
         * - Добавлен shared-промис (_initPromise), чтобы исключить параллельные вызовы initAuth
         *   из разных мест (роут-гарды, main и т.д.). Повторные вызовы будут ожидать один промис.
         * - Логика проверки токена осталась прежней: валидный access -> загрузка профиля/прав,
         *   иначе — попытка refresh, при ошибке — очистка токенов.
         * - Флаг isInitialized проставляется один раз по завершении (успех/ошибка).
         * - verifyJWT вызывается с clockSkew=30 для предотвращения использования токена,
         *   который скоро истечёт.
         */
        async initAuth() {
            if (this.isInitialized) {
                return;
            }
            if (this._initPromise) {
                return this._initPromise;
            }

            this._initPromise = (async () => {
                try {
                    const token = localStorage.getItem(AUTH_TOKEN_KEY);
                    if (token) {
                        const verified = verifyJWT(token, 30); // clockSkew 30 секунд
                        if (verified?.valid) {
                            this.access_token = token;
                            this.userId = this.userId = verified.payload.ID || verified.payload.sub || verified?.payload?.user_id || 0;

                            await this.fetchUserInfo(false);
                            await this.fetchUserPermissions();
                        } else {
                            try {
                                await this.refreshToken();
                            } catch (error) {
                                console.error("Ошибка при обновлении токена:", error);
                                this.clearToken();
                            }
                        }
                    }
                } catch (error) {
                    console.error("Ошибка при инициализации аутентификации:", error);
                } finally {
                    this.isInitialized = true;
                    this._initPromise = null;
                }
            })();

            return this._initPromise;
        },
        /**
         * Обновление токена.
         *
         * Внимание: основной механизм refresh реализован в axios interceptors.
         * Этот метод используется только при инициализации приложения (initAuth).
         * При получении 401 в axios interceptors refresh вызывается автоматически.
         */
        async refreshToken() {
            try {
                // localStorage — источник правды, а не состояние стора: axios-интерсептор
                // при ротации пишет новый refresh-токен именно туда, и состояние других
                // вкладок остаётся протухшим. Отправка протухшего токена дала бы 401 на
                // /auth/refresh, а интерсептор на этой ветке разлогинивает пользователя.
                const refreshToken = localStorage.getItem(REFRESH_TOKEN) || this.refresh_token;
                const response = await api.post('/auth/refresh', {refresh_token: refreshToken});
                this.setToken(response.data);
                await this.fetchUserInfo(false);
                await this.fetchUserPermissions({force: true});
            } catch (error) {
                // Игнорируем ошибки отмены запроса
                if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
                    return;
                }

                this.clearToken();
                throw new Error('Ошибка при обновлении токена; Error:' + error);
            }
        },
        setToken(payload) {
            // Поддерживаем оба варианта структуры ответа:
            // 1) { access_token, refresh_token }
            // 2) { status, message, data: { access_token, refresh_token } }
            const data = payload?.data ?? payload;

            const accessToken = data?.access_token;
            const refreshToken = data?.refresh_token;
            const userID = data?.user_id;

            if (!accessToken) {
                console.error("Invalid data for setting token:", payload);
                this.clearToken();
                return;
            }

            this.access_token = accessToken;
            if (refreshToken) {
                this.refresh_token = refreshToken;
                localStorage.setItem(REFRESH_TOKEN, refreshToken);
            }

            localStorage.setItem(AUTH_TOKEN_KEY, this.access_token);
            const verified = verifyJWT(this.access_token, 30);
            if (verified?.valid) {
                this.userId = data?.user_id || verified.payload.ID || verified.payload.sub || 0;
            } else {
                this.userId = 0;
            }
            // Инвалидируем кэш прав, чтобы получить актуальные
            this.permissionsFetchedAt = null;
        },
        clearToken() {
            Object.assign(this, {
                access_token: null,
                refresh_token: null,
                userRoles: [],
                userId: 0,
                isInitialized: false,
                permissionsFetchedAt: null,
                _permissionsPromise: null,
            });
            localStorage.removeItem(AUTH_TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN);
        },
        async login(email, password) {
            const response = await api.post('/auth/login', {email, password});
            this.setToken(response.data);
            await this.fetchUserInfo(true);
            await this.fetchUserPermissions({force: true});
        },
        async logout() {
            this.clearToken();
            const {useNavigation} = await import("@/router/navigation.js");
            const {navigateTo, routes} = useNavigation();
            navigateTo(routes.HOME);
        },
        async fetchUserInfo(showWelcome = false) {
            if (!this.userId) return;

            try {
                const userStore = useUserStore();
                await userStore.fetchById(this.userId, { target: 'current' });

                if (showWelcome) {
                    const displayName = userStore.getUserDisplayName || 'Пользователь';
                    notifier.success(TOAST_OPTIONS.success, `Добро пожаловать, ${displayName}!`, 3000);
                }
            } catch (error) {
                // Игнорируем ошибки отмены запроса
                if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
                    return;
                }

                console.error('Ошибка при получении информации о пользователе:', error);
            }
        },
        async registration({
                               username,
                               email,
                               password,
                               confirmPassword,
                               referralCode,
                           }) {
            return await api.post('/auth/register',
                {
                    username: username,
                    email: email,
                    password: password,
                    password_confirm: confirmPassword,
                    referral_code: referralCode,
                });
        },
        async confirmEmail(activation_code) {
            const response = await api.get(`/auth/confirm?token=${activation_code}`);

            // Access-токен на руках всё ещё несёт старый статус (not_confirmed) —
            // без перевыпуска write-запросы будут отдавать 403 EMAIL_NOT_CONFIRMED
            // ещё до 15 минут. Ошибка refresh не должна ломать экран подтверждения.
            if (localStorage.getItem(REFRESH_TOKEN)) {
                try {
                    await this.refreshToken();
                } catch (error) {
                    console.error('Ошибка обновления токена после подтверждения email:', error);
                }
            }

            return response;
        },
        async sendActivationCode() {
            return await api.post('/profile/send-activate-code');
        },
        async restorePassword(email) {
            return await api.post('/auth/password-reset/request', {email});
        },
        async resetPassword(email, password, code) {
            return await api.post('/auth/password-reset/confirm', {
                email,
                password,
                code
            });
        },
        hasPermission(permissions = []) {
            if (!Array.isArray(permissions)) {
                return false;
            }

            if (this.userRoles.length === 0) {
                return false;
            }

            return permissions.some(permission => {
                return this.userRoles.some(userRole => userRole.permission_name === permission);
            });
        },
        hasRole(roles = []) {
            if (this.userRoles.length === 0) {
                return false;
            }
            return roles.some(role => {
                return this.userRoles.some(userRole => userRole.role_name === role);
            });
        },
        requireAuth() {
            if (!this.isAuthenticated) {
                notifier.warn(NOTIFY.requireAuth, 'Для выполнения этого действия необходимо войти в систему', 4000);
                (async () => {
                    const {useNavigation} = await import("@/router/navigation.js");
                    const {navigateTo, routes} = useNavigation();
                    navigateTo(routes.LOGIN);
                })();
                return false;
            }
            return true;
        },
        /**
         * Гейт для действий, которые бэкенд блокирует пользователям с неподтверждённой
         * почтой/забаненным (middleware.RequireConfirmedEmail) — публикация объявлений,
         * чат, отклики и т.д. Проверяем статус локально из клеймов JWT, чтобы показать
         * тост сразу, до захода в форму/отправки запроса, а не после 403 с бэкенда.
         */
        requireConfirmedEmail() {
            if (!this.requireAuth()) {
                return false;
            }

            const verified = verifyJWT(this.access_token);
            if (!verified?.valid) {
                // Токен протух/невалиден — актуальный статус подтянется обычным циклом
                // refresh в axios-интерсепторе на самом запросе, здесь не блокируем сами.
                return true;
            }

            const {Status: status, Role: role} = verified.payload || {};

            if (role === ROLE_ADMIN || status === 'active') {
                return true;
            }

            notifier.warn(
                NOTIFY.requireConfirmedEmail,
                status === 'banned'
                    ? 'Ваш аккаунт заблокирован'
                    : 'Подтвердите почту, чтобы выполнить это действие — код подтверждения можно запросить в настройках профиля',
                4500
            );
            return false;
        },
    },
    getters: {
        isAuthenticated: (state) => !!state.access_token,
        isPermissionsLoaded: (state) => state.permissionsFetchedAt !== null,
    },
});
